/** Selling & Disclosure section on the deal drawer (v1 selling-ai.js): the
 * Repair Addendum tracker + printable Repair Completion Report, the Florida
 * Seller's Property Disclosure builder, and the MLS Public Remarks generator —
 * each rebuilt to match v1's layout (stat tiles, category-grouped repair cards,
 * the "DATA THE AI WILL USE" card). State lives on the deal edits (v1
 * card.edits) via onSaveBlob → the deals door. */
import { useEffect, useRef, useState } from "react";
import { moneyExact } from "../../lib/money";
import type { Deal } from "../deals/api";
import type { SowState } from "../sow/api";
import { useConfirmDelete } from "../common/ConfirmDelete";
import {
  repairsOf, invoicesOf, addendumsOf, disclosureOf, discNotesOf, discCounts, repairId,
  REPAIR_CATS, REPAIR_TRADES, REPAIR_STATUS, SEL_DISC,
  completionReportHtml, disclosureDocHtml, openPrintable, fileToDataUrl,
  remarksFacts, buildRemarks, narrowSelRemarks, REMARKS_TONES, REMARKS_LENGTHS,
  type SelRepair, type SelInvoice, type SelAddendum, type ParsedRepair,
  type RemarksFacts, type RemarksTone, type RemarksLength,
} from "./api";

const todayStr = () => new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
const money = (n: number) => moneyExact(n);
/** category → color-class slug for the repair-group badge (v1 CATC). */
const catClass = (c: string) => "c-" + c.toLowerCase().replace(/[^a-z]/g, "");

export interface SellingAi {
  canFire: boolean;
  parseAddendum: (pdfB64: string) => Promise<ParsedRepair[]>;
  parseInvoice: (fileB64: string, mime: string, repairs: { id: string; title: string }[]) => Promise<{ vendor: string; amount: number; desc: string; matchedIds: string[] }>;
  /** v1 /api/selling/remarks — AI-written MLS copy from the gathered facts. */
  remarks: (facts: RemarksFacts, sowItems: number, tone: RemarksTone, len: RemarksLength, notes: string) => Promise<string>;
}

type InvDraft = { vendor: string; amount: string; desc: string; linked: string[] };

export function SellingSection({ deal, canEdit, onSaveBlob, ai, sub, fetchSow }: {
  deal: Deal;
  canEdit: boolean;
  onSaveBlob: (natKey: string, key: string, value: unknown) => void;
  ai: SellingAi;
  /** v1 Inside-Selling sub-tab: render just one piece, always-expanded.
   * Omit = all three stacked (backward-compatible). */
  sub?: "disclosure" | "repair" | "remarks";
  /** the deal's SOW, fetched lazily to feed the Public-Remarks highlights. */
  fetchSow?: (dealKey: string) => Promise<SowState | null>;
}) {
  const [newRep, setNewRep] = useState("");
  const [adding, setAdding] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [aiBusy, setAiBusy] = useState("");
  const [aiNote, setAiNote] = useState<{ text: string; err: boolean } | null>(null);
  const [invDraft, setInvDraft] = useState<InvDraft | null>(null);
  const addRef = useRef<HTMLInputElement | null>(null);
  const invRef = useRef<HTMLInputElement | null>(null);

  // v1 typed-delete: every destructive action confirms via the type-"delete" modal.
  const confirmDel = useConfirmDelete();

  const fields = deal.fields;
  const repairs = repairsOf(fields);
  const invoices = invoicesOf(fields);
  const addendums = addendumsOf(fields);
  const disclosure = disclosureOf(fields);
  const notes = discNotesOf(fields);
  const seller = typeof fields["selDiscSeller"] === "string" ? (fields["selDiscSeller"] as string) : "";
  const date = typeof fields["selDiscDate"] === "string" && fields["selDiscDate"] ? (fields["selDiscDate"] as string) : new Date().toLocaleDateString("en-US");
  const { answered, total } = discCounts(disclosure);
  const doneCount = repairs.filter((r) => r.st === "Completed").length;
  const pct = repairs.length ? Math.round((doneCount / repairs.length) * 100) : 0;
  const totalCost = invoices.reduce((s, v) => s + (v.amount || 0), 0) + repairs.reduce((s, r) => s + (r.cost || 0), 0);
  const invTotal = invoices.reduce((s, v) => s + (v.amount || 0), 0);
  const cats = [...new Set(repairs.map((r) => r.cat || "General"))];

  const saveRepairs = (list: SelRepair[]) => onSaveBlob(deal.natKey, "selRepairs", list);
  const addRepair = () => {
    const t = newRep.trim();
    if (!t) return;
    saveRepairs([...repairs, { id: repairId(), t, cat: "General", who: "General Contractor", st: "Pending", cost: 0, desc: "" }]);
    setNewRep("");
    setAdding(false);
  };
  const patchRepair = (id: string, patch: Partial<SelRepair>) => saveRepairs(repairs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  // v1 selRepDel / selInvDel / selAddDel — all typed-delete gated, same wording.
  const delRepair = (id: string) => confirmDel.ask("Delete this repair item?", () => saveRepairs(repairs.filter((r) => r.id !== id)));
  const saveInvoices = (list: SelInvoice[]) => onSaveBlob(deal.natKey, "selInvoices", list);
  const delInvoice = (id: string) => confirmDel.ask("Delete this invoice? (Repairs it completed stay completed.)", () => saveInvoices(invoices.filter((v) => v.id !== id)));
  const saveAddendums = (list: SelAddendum[]) => onSaveBlob(deal.natKey, "selAddendums", list);
  const delAddendum = (i: number) => confirmDel.ask("Remove this addendum record? (Its extracted repair items stay.)", () => saveAddendums(addendums.filter((_, k) => k !== i)));

  const errText = (e: unknown) => (e instanceof Error ? e.message : "Backend unreachable");

  const importAddendum = async (file: File) => {
    if (!ai.canFire) return;
    setAiBusy("add"); setAiNote({ text: "Reading addendum with AI…", err: false });
    try {
      const parsed = await ai.parseAddendum(await fileToDataUrl(file));
      const have = new Set(repairs.map((r) => r.t.toLowerCase()));
      const fresh = parsed.filter((p: ParsedRepair) => !have.has(p.title.toLowerCase()))
        .map((p: ParsedRepair) => ({ id: repairId(), t: p.title, cat: p.category, who: p.contractor, st: "Pending", cost: 0, desc: "" }));
      if (fresh.length) saveRepairs([...repairs, ...fresh]);
      // v1 records every imported addendum → the "Repair Addendum Documents" card.
      saveAddendums([...addendums, { name: file.name, n: fresh.length, at: Date.now() }]);
      setAiNote({ text: `${fresh.length} repair item(s) extracted from "${file.name}" ✓`, err: false });
    } catch (e) { setAiNote({ text: "Addendum parse failed: " + errText(e), err: true }); }
    finally { setAiBusy(""); if (addRef.current) addRef.current.value = ""; }
  };

  const importInvoice = async (file: File) => {
    if (!ai.canFire) return;
    setAiBusy("inv"); setAiNote({ text: "Reading invoice with AI…", err: false });
    try {
      const r = await ai.parseInvoice(await fileToDataUrl(file), file.type || "application/pdf", repairs.map((x) => ({ id: x.id, title: x.t })));
      setInvDraft({ vendor: r.vendor || file.name, amount: r.amount ? String(r.amount) : "", desc: r.desc, linked: r.matchedIds });
      setAiNote(null);
    } catch (e) { setAiNote({ text: "Invoice parse failed: " + errText(e), err: true }); }
    finally { setAiBusy(""); if (invRef.current) invRef.current.value = ""; }
  };

  const saveInvoiceDraft = () => {
    if (!invDraft) return;
    const inv: SelInvoice = { id: repairId(), vendor: invDraft.vendor || "Invoice", amount: Number(String(invDraft.amount).replace(/[^0-9.]/g, "")) || 0, desc: invDraft.desc, linked: invDraft.linked };
    saveInvoices([...invoices, inv]);
    const linked = new Set(invDraft.linked);
    const marked = repairs.filter((r) => linked.has(r.id) && r.st !== "Completed");
    if (marked.length) saveRepairs(repairs.map((r) => (linked.has(r.id) ? { ...r, st: "Completed" } : r)));
    setInvDraft(null);
    setAiNote({ text: `Invoice saved — ${marked.length} repair(s) marked done ✓`, err: false });
  };

  const setAnswer = (id: string, v: string) => onSaveBlob(deal.natKey, "selDisclosure", { ...disclosure, [id]: v });
  const setNote = (id: string, v: string) => onSaveBlob(deal.natKey, "selDiscNotes", { ...notes, [id]: v });
  const fillNo = () => {
    const next = { ...disclosure };
    for (const [, qs] of SEL_DISC) for (const [id] of qs) if (!next[id]) next[id] = "No";
    onSaveBlob(deal.natKey, "selDisclosure", next);
  };

  const genReport = () => { if (!openPrintable(completionReportHtml(deal.address, repairs, invoices, todayStr()))) alert("Allow pop-ups to generate the report."); };
  const genDoc = () => { if (!openPrintable(disclosureDocHtml(deal.address, seller, date, disclosure, notes))) alert("Allow pop-ups to generate the document."); };

  // v1 Inside-Selling sub-tabs: when `sub` is set, render exactly one piece.
  const showRep = sub === undefined || sub === "repair";
  const showDisc = sub === undefined || sub === "disclosure";
  const showRem = sub === undefined || sub === "remarks";

  return (
    <>
      {showRep ? (
      <div className="selbody">
        {/* Repair Completion Report card (v1 top card) */}
        <div className="selreport">
          <div className="selreportmain">
            <div className="selreporttitle">📄 Repair Completion Report</div>
            <div className="selreportsub">PDF with all repairs, costs, and invoices for the buyer's agent</div>
          </div>
          <button className="btn slim primary" onClick={genReport} disabled={repairs.length === 0}>Generate Report</button>
        </div>

        {/* Repair Addendum Documents (v1 selAddendums card — one row per imported PDF) */}
        {addendums.length ? (
          <div className="seladdocs">
            <div className="seladdocst">📁 Repair Addendum Documents</div>
            {addendums.map((a, i) => (
              <div key={i} className="seladdoc">
                <span className="seladdocic">📄</span>
                <div className="seladdocmain">
                  <div className="seladdocname">{a.name}</div>
                  <div className="seladdocsub">{a.n} repair item(s) extracted{a.at ? " " + new Date(a.at).toLocaleDateString() : ""}</div>
                </div>
                {canEdit ? <button className="rowbtn del" title="Remove addendum record" onClick={() => delAddendum(i)}>✕</button> : null}
              </div>
            ))}
          </div>
        ) : null}

        {/* Stat tiles + progress (v1 3-up grid) */}
        <div className="selstats">
          <div className="selstat"><div className="selstatl">🔧 Total items</div><div className="selstatv">{repairs.length}</div></div>
          <div className="selstat"><div className="selstatl">✅ Completed</div><div className="selstatv">{doneCount}/{repairs.length}{repairs.length ? <span className="selstatpct">{pct}%</span> : null}</div></div>
          <div className="selstat"><div className="selstatl">💵 Total cost</div><div className="selstatv">{money(totalCost)}</div></div>
        </div>
        {repairs.length ? <div className="selbar"><div className="selbarfill" style={{ width: pct + "%" }} /></div> : null}

        {/* Import from Repair Addendum (v1 blue card) */}
        {canEdit ? (
          <div className="selimport">
            <div className="selimporttitle">✨ Import from Repair Addendum</div>
            <div className="selimportsub">Upload the buyer's repair addendum PDF and AI will extract the repair tasks automatically.</div>
            {ai.canFire ? (
              <label className="selimportbtn">
                {aiBusy === "add" ? "Reading…" : "⬆ Select Addendum PDF"}
                <input ref={addRef} type="file" accept="application/pdf" disabled={!!aiBusy} hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void importAddendum(f); }} />
              </label>
            ) : <span className="inbxgate">AI import turns on with the Selling-AI enable</span>}
          </div>
        ) : null}

        {/* Repair Items header + add */}
        <div className="selitemshead">
          <span className="selitemsh">Repair Items {repairs.length ? <span className="secn">{doneCount}/{repairs.length} done</span> : null}</span>
          {canEdit ? <button className="btn slim primary" onClick={() => setAdding((v) => !v)}>+ Add Item</button> : null}
        </div>
        {adding && canEdit ? (
          <div className="selrepadd">
            <input autoFocus value={newRep} placeholder="Repair item description…" onChange={(e) => setNewRep(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addRepair(); if (e.key === "Escape") { setAdding(false); setNewRep(""); } }} />
            <button className="btn slim primary" onClick={addRepair} disabled={!newRep.trim()}>Add</button>
            <button className="btn slim" onClick={() => { setAdding(false); setNewRep(""); }}>Cancel</button>
          </div>
        ) : null}

        {/* Items grouped by category (v1) */}
        {repairs.length === 0 ? (
          <div className="selempty">No repair items yet — import the buyer's addendum PDF above, or add items manually.</div>
        ) : (
          cats.map((cat) => {
            const list = repairs.filter((r) => (r.cat || "General") === cat);
            const cd = list.filter((r) => r.st === "Completed").length;
            return (
              <div key={cat} className="selcatgroup">
                <div className="selcathead"><span className={"selcatbadge " + catClass(cat)}>{cat}</span><span className="secn">{cd}/{list.length} done</span></div>
                {list.map((r) => {
                  const open = expanded === r.id;
                  const linked = invoices.filter((v) => v.linked.includes(r.id));
                  return (
                    <div key={r.id} className={"selitem" + (r.st === "Completed" ? " done" : "")}>
                      <div className="selitemtop">
                        <button className={"selitemchk" + (r.st === "Completed" ? " on" : "")} title="Toggle completed" disabled={!canEdit} onClick={() => patchRepair(r.id, { st: r.st === "Completed" ? "Pending" : "Completed" })}>✓</button>
                        <div className="selitemmain">
                          <div className="selitemt">{r.t}{r.st === "Completed" ? <span className="selitemdone">Completed</span> : null}</div>
                          <div className="selitempills">
                            <span className="selpill amber">🔧 {r.who || "General Contractor"}</span>
                            {r.cost ? <span className="selpill slate">{money(r.cost)}</span> : null}
                            {linked.length ? <span className="selpill emerald">🧾 {linked.length}</span> : null}
                          </div>
                        </div>
                        <div className="selitemacts">
                          <button className="rowbtn" title={open ? "Collapse" : "Edit"} onClick={() => setExpanded(open ? null : r.id)}>{open ? "▲" : "▾"}</button>
                          {canEdit ? <button className="rowbtn del" title="Delete" onClick={() => delRepair(r.id)}>✕</button> : null}
                        </div>
                      </div>
                      {open ? (
                        <div className="selitemexp">
                          <label><span>Status</span><select value={r.st} disabled={!canEdit} onChange={(e) => patchRepair(r.id, { st: e.target.value })}>{REPAIR_STATUS.map((c) => <option key={c}>{c}</option>)}</select></label>
                          <label><span>Category</span><select value={r.cat} disabled={!canEdit} onChange={(e) => patchRepair(r.id, { cat: e.target.value })}>{REPAIR_CATS.map((c) => <option key={c}>{c}</option>)}</select></label>
                          <label><span>Cost ($)</span><input type="number" inputMode="decimal" value={r.cost || ""} disabled={!canEdit} placeholder="0" onChange={(e) => patchRepair(r.id, { cost: Number(e.target.value) || 0 })} /></label>
                          <label><span>Contractor</span><select value={r.who} disabled={!canEdit} onChange={(e) => patchRepair(r.id, { who: e.target.value })}>{REPAIR_TRADES.map((c) => <option key={c}>{c}</option>)}</select></label>
                          <label className="full"><span>Description / notes</span><input value={r.desc} disabled={!canEdit} placeholder="—" onChange={(e) => patchRepair(r.id, { desc: e.target.value })} /></label>
                          {linked.length ? <div className="selitemlinked">{linked.map((v) => <div key={v.id}>🧾 {v.vendor} — {money(v.amount)}</div>)}</div> : null}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            );
          })
        )}

        {/* Invoices card (v1) */}
        <div className="selinvcard">
          <div className="selinvhead">
            <span className="selinvtitle">🧾 Invoices <span className="secn">({invoices.length})</span>{invoices.length ? <span className="selinvtotal">Total: {money(invTotal)}</span> : null}</span>
            {canEdit && ai.canFire ? (
              <label className="btn slim primary selupload">
                {aiBusy === "inv" ? "Reading…" : "+ Upload Invoice"}
                <input ref={invRef} type="file" accept="application/pdf,image/*" disabled={!!aiBusy} hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void importInvoice(f); }} />
              </label>
            ) : null}
          </div>
          <div className="selinvsub">AI reads the invoice, fills vendor + amount, and marks the matching repair items done.</div>

          {invDraft ? (
            <div className="expform selinvform">
              <div className="expformtitle">Confirm invoice — AI pre-checked its repair matches</div>
              <div className="expformgrid">
                <label><span>Vendor</span><input value={invDraft.vendor} onChange={(e) => setInvDraft({ ...invDraft, vendor: e.target.value })} /></label>
                <label><span>Amount ($)</span><input type="number" inputMode="decimal" value={invDraft.amount} onChange={(e) => setInvDraft({ ...invDraft, amount: e.target.value })} /></label>
                <label className="wide"><span>Description</span><input value={invDraft.desc} onChange={(e) => setInvDraft({ ...invDraft, desc: e.target.value })} /></label>
              </div>
              {repairs.length ? (
                <div className="selinvcovers">
                  <div className="scgrouptitle">Covers these repairs (checked = marked done)</div>
                  {repairs.map((r) => {
                    const on = invDraft.linked.includes(r.id);
                    return (
                      <label key={r.id} className="selinvcheck">
                        <input type="checkbox" checked={on} onChange={() => setInvDraft({ ...invDraft, linked: on ? invDraft.linked.filter((x) => x !== r.id) : [...invDraft.linked, r.id] })} />
                        <span className={r.st === "Completed" ? "dim" : ""}>{r.t}</span>
                      </label>
                    );
                  })}
                </div>
              ) : null}
              <div className="expformacts">
                <button className="btn slim primary" onClick={saveInvoiceDraft}>Save &amp; mark done</button>
                <button className="btn slim" onClick={() => setInvDraft(null)}>Cancel</button>
              </div>
            </div>
          ) : null}

          {invoices.length === 0 && !invDraft ? (
            <div className="selinvempty">No invoices yet — upload contractor invoices and they link to repair items automatically.</div>
          ) : null}
          {invoices.map((v) => (
            <div key={v.id} className="selinvitem">
              <div className="selinvitemmain">
                <div className="selinvitemtop"><span className="selinvv">{v.vendor}</span> <span className="selinva">{money(v.amount)}</span></div>
                {v.desc ? <div className="selinvdesc">{v.desc}</div> : null}
                {v.linked.length ? (
                  <div className="selinvchips">
                    {v.linked.map((id) => { const r = repairs.find((x) => x.id === id); return r ? <span key={id} className="selinvchip">{r.t.length > 40 ? r.t.slice(0, 40) + "…" : r.t}</span> : null; })}
                  </div>
                ) : null}
              </div>
              {canEdit ? <button className="rowbtn del" title="Delete invoice" onClick={() => delInvoice(v.id)}>✕</button> : null}
            </div>
          ))}
        </div>

        {aiNote ? <div className={"inbxnote" + (aiNote.err ? " err" : "")}>{aiNote.text}</div> : null}
      </div>
      ) : null}

      {showDisc ? (
      <div className="selbody">
        <div className="seldischead2">
          <div className="seldisctitlebig">📝 Florida Seller's Property Disclosure <span className="seldisctag">(SPDR-style)</span></div>
          <div className="expformacts">
            {canEdit ? <button className="btn slim" onClick={fillNo}>Fill unanswered with "No"</button> : null}
            <button className="btn slim primary" onClick={genDoc}>🖨 Generate Document</button>
          </div>
        </div>
        <div className="seldisclegend">
          <b>{answered}/{total}</b> answered · <span className="lg-y">Yes = condition exists</span> · <span className="lg-n">No</span> · <span className="lg-u">? = unknown</span>. Answers save automatically.
        </div>
        <div className="seldischead">
          <label><span>Seller / entity name</span><input defaultValue={seller} disabled={!canEdit} placeholder="e.g. DLS Property Holdings LLC" onBlur={(e) => e.target.value !== seller && onSaveBlob(deal.natKey, "selDiscSeller", e.target.value)} /></label>
          <label><span>Date</span><input defaultValue={date} disabled={!canEdit} onBlur={(e) => e.target.value !== date && onSaveBlob(deal.natKey, "selDiscDate", e.target.value)} /></label>
        </div>
        {SEL_DISC.map(([sec, qs]) => (
          <div key={sec} className="seldiscsec">
            <div className="seldisctitle">{sec} <span className="secn">{qs.filter(([id]) => disclosure[id]).length}/{qs.length}</span></div>
            {qs.map(([id, q]) => (
              <div key={id} className="seldiscq">
                <div className="seldiscrow">
                  <span className="seldiscqt">{q}</span>
                  <div className="seldiscans">
                    {(["Yes", "No", "?"] as const).map((o) => (
                      <button key={o} className={"seldiscbtn a-" + (o === "?" ? "u" : o.toLowerCase()) + (disclosure[id] === o ? " on" : "")} disabled={!canEdit} onClick={() => setAnswer(id, o)}>{o}</button>
                    ))}
                  </div>
                </div>
                {disclosure[id] === "Yes" || disclosure[id] === "?" ? (
                  <input className="seldiscnote" defaultValue={notes[id] ?? ""} disabled={!canEdit} placeholder="Explain (shown on the document)…" onBlur={(e) => e.target.value !== (notes[id] ?? "") && setNote(id, e.target.value)} />
                ) : null}
              </div>
            ))}
          </div>
        ))}
      </div>
      ) : null}

      {showRem ? <MlsRemarks deal={deal} canEdit={canEdit} onSaveBlob={onSaveBlob} ai={ai} {...(fetchSow ? { fetchSow } : {})} /> : null}
      {confirmDel.modal}
    </>
  );
}

/** MLS Public Remarks generator (v1 sellingRemarksPanel). Generate calls the
 * real /api/selling/remarks AI like v1 (when the selling-AI gate is on) and
 * falls back to the deterministic local writer offline — either way the output
 * is fully editable and saved to the deal's selRemarks blob. The "DATA THE AI
 * WILL USE" card previews the exact facts that shape the copy. */
function MlsRemarks({ deal, canEdit, onSaveBlob, ai, fetchSow }: {
  deal: Deal;
  canEdit: boolean;
  onSaveBlob: (natKey: string, key: string, value: unknown) => void;
  ai: SellingAi;
  fetchSow?: (dealKey: string) => Promise<SowState | null>;
}) {
  const saved = narrowSelRemarks(deal.fields["selRemarks"]);
  const [tone, setTone] = useState<RemarksTone>(saved?.tone ?? "Standard");
  const [len, setLen] = useState<RemarksLength>(saved?.len ?? "Standard");
  const [notes, setNotes] = useState(saved?.notes ?? "");
  const [text, setText] = useState(saved?.text ?? "");
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [genNote, setGenNote] = useState("");
  const [sowNames, setSowNames] = useState<string[]>([]);

  // v1 _selRemarksFacts pulled SOW item names from window.SCOPES; here we fetch
  // the deal's SOW lazily and feed the item names into the fact builder.
  useEffect(() => {
    if (!fetchSow) return;
    let alive = true;
    void fetchSow(deal.natKey).then((s) => { if (alive && s) setSowNames(s.items.map((i) => i.name)); }).catch(() => { /* offline: no highlights */ });
    return () => { alive = false; };
  }, [deal.natKey, fetchSow]);

  const facts = remarksFacts(deal, sowNames);
  const factLine = [
    facts.beds && `${facts.beds} bed`, facts.baths && `${facts.baths} bath`, facts.sqft && `${facts.sqft} sqft`,
  ].filter(Boolean).join(" · ");
  const sysLine = [facts.roof && `Roof ${facts.roof}`, facts.hvac && `HVAC ${facts.hvac}`, facts.hwh && `HWH ${facts.hwh}`].filter(Boolean).join(" · ");

  // v1 selRemarksGen: backend AI writes the copy and the result saves to the
  // deal right away. Offline / gate-off → the deterministic local writer.
  const generate = async () => {
    let out: string;
    if (ai.canFire) {
      setBusy(true); setGenNote("");
      try {
        out = await ai.remarks(facts, sowNames.length, tone, len, notes);
      } catch (e) {
        out = buildRemarks(facts, tone, len, notes);
        setGenNote("AI writer unreachable (" + (e instanceof Error ? e.message : "backend error") + ") — used the built-in writer instead.");
      } finally { setBusy(false); }
    } else {
      out = buildRemarks(facts, tone, len, notes);
    }
    setText(out);
    if (canEdit) onSaveBlob(deal.natKey, "selRemarks", { text: out, tone, len, notes, at: Date.now() });
  };
  const save = () => onSaveBlob(deal.natKey, "selRemarks", { text, tone, len, notes, at: Date.now() });
  const copy = () => { try { void navigator.clipboard?.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1400); } catch { /* ignore */ } };
  const words = text.split(/\s+/).filter(Boolean).length;

  const Seg = <T extends string>(cur: T, set: (v: T) => void, opts: { v: T; sub: string }[]) => (
    <div className="remseg">
      {opts.map((o) => (
        <button key={o.v} className={"remsegbtn" + (cur === o.v ? " on" : "")} onClick={() => set(o.v)}>
          {o.v}<span>{o.sub}</span>
        </button>
      ))}
    </div>
  );

  return (
    <div className="selbody rembody">
      <div className="remhead">
        <div className="remtitle">✨ MLS Public Remarks</div>
        <div className="remsub">AI-ready description for your MLS listing, built from Scope of Work, Rehab-tab systems, HOA, and property data.{saved && saved.at ? <span className="remsaved"> · Last saved {new Date(saved.at).toLocaleString()}</span> : null}</div>
      </div>

      {/* DATA THE AI WILL USE (v1 white card) */}
      <div className="remdata">
        <div className="remdatal">ⓘ DATA THE AI WILL USE</div>
        <div className="remdatagrid">
          <div><b>📍 Property</b><span>{facts.address}<br />{factLine || "Enter beds/baths/sqft on the Property tab"}{facts.year ? <><br />Built {facts.year}</> : null}</span></div>
          <div><b>🔧 Systems (from Rehab tab)</b><span>{sysLine || "Fill in system ages on the Rehab tab"}</span></div>
          <div><b>🔨 Scope of Work</b><span>{facts.highlights.length ? `${facts.highlights.length} renovation highlights from ${sowNames.length} line items` : "No SOW for this deal yet"}</span></div>
          <div><b>🏘 HOA</b><span>{facts.hoa || "—"}</span></div>
        </div>
      </div>

      <div className="remopts">
        <div><label className="lbl">Tone</label>{Seg(tone, setTone, REMARKS_TONES.map((t) => ({ v: t.tone, sub: t.sub })))}</div>
        <div><label className="lbl">Length</label>{Seg(len, setLen, REMARKS_LENGTHS.map((l) => ({ v: l.len, sub: l.sub })))}</div>
      </div>
      <label className="lbl">Extra notes <span className="sub">(optional — incorporate things not captured elsewhere)</span></label>
      <textarea className="notes" rows={2} value={notes} placeholder="e.g. backs to conservation, new pool pump, fresh sod…" onChange={(e) => setNotes(e.target.value)} />
      <button className="btn slim primary remgen" disabled={busy} onClick={() => void generate()}>{busy ? "✨ Writing…" : text ? "Regenerate" : "✨ Generate Public Remarks"}</button>
      {genNote ? <div className="inbxnote err">{genNote}</div> : null}
      {text ? (
        <div className="remoutcard">
          <div className="remouthead"><span className="fld-l">Output</span><span className="remcount">{words} words · {text.length} / 4000 chars</span></div>
          <textarea className="notes remout" rows={8} value={text} onChange={(e) => setText(e.target.value)} />
          <div className="rembtns">
            <button className="btn slim" onClick={copy}>{copied ? "Copied ✓" : "Copy"}</button>
            {canEdit ? <button className="btn slim" onClick={save}>Save edits</button> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
